// ============================================================
// root-route.ts
// ============================================================
// Pure decision logic for the "/" route in App.tsx.
//
// Bug this fixes (ticket #817 — "long blank white screen on load"):
// "/" is listed in PUBLIC_PREFIXES so logged-out WEB visitors see the
// marketing Landing page immediately, without waiting on the auth-loading
// gate. On NATIVE, "/" never renders Landing — it always redirects to
// either /auth or /dashboard. Because it inherited the public-route bypass,
// a native cold launch evaluated that redirect immediately, while
// `loading` was still true and the real session hadn't resolved yet, so
// `hasUserAndProfile` was false — sending an already-authenticated user to
// /auth. auth resolving a moment later then bounced them back to
// /dashboard. Two full route mounts (each with its own effects, styles and
// paint) stacked on top of the native splash's fixed-timer hide is what
// read as a long blank/flashing screen before the dashboard finally
// appeared.
//
// Fix: on native, wait for `loading` to resolve before choosing between
// /auth and /dashboard. Null means "not ready yet" — the caller keeps
// showing the same loading UI used for every other protected route.

export type RootRouteTarget =
  | 'limited'
  | 'super-admin'
  | 'dashboard'
  | 'auth'
  | 'landing'
  | null;

export interface RootRouteParams {
  isNative: boolean;
  loading: boolean;
  hasUserAndProfile: boolean;
  isSuperAdmin: boolean;
  /** loginMethod === 'pin' && profile && !user */
  isPinSession: boolean;
  /** role === 'employee' && a valid PIN session is also active */
  isEmployeeWithValidPinSession: boolean;
  /**
   * This device holds a kiosk DEVICE session (`kiosk_unlocked.session_token`),
   * i.e. someone with kiosk rights explicitly turned this tablet into a kiosk.
   */
  hasKioskDeviceSession: boolean;
}

export function resolveRootRoute(params: RootRouteParams): RootRouteTarget {
  const {
    isNative,
    loading,
    hasUserAndProfile,
    isSuperAdmin,
    isPinSession,
    isEmployeeWithValidPinSession,
    hasKioskDeviceSession,
  } = params;

  if (isPinSession || isEmployeeWithValidPinSession) return 'limited';

  // A kiosk tablet belongs on the PIN pad, signed in or not.
  //
  // `kiosk-launch` signs the launcher's email session out of the device (#1429),
  // which is right — but it left the device with NO session at all, so the next
  // cold start fell through to /auth and the store tablet asked for an email and
  // password. Bijan hit exactly that after #1429/#1430 shipped: "whenever i close
  // the app it still requires my email and password but after entering them in it
  // goes to kiosk mode" — the email login was a toll booth in front of a screen
  // that never needed it, and the only reason it ended at the kiosk afterwards is
  // that #1430's self-heal then evicted the session he had just created.
  //
  // Checked BEFORE `hasUserAndProfile` on purpose: a device that somehow still
  // carries an email session is in the state #1430 heals, and sending it to
  // /dashboard first only renders a dashboard that is about to be torn down.
  //
  // The signal is the DEVICE session, never a leftover `pin_session` — see the
  // long note in kiosk-surface.ts. A stray PIN key on someone's phone is not a
  // kiosk, and treating it as one strands them on a PIN pad (#1129). Ending kiosk
  // mode or revoking the device clears the key and the app behaves normally again.
  if (hasKioskDeviceSession) return 'limited';

  if (hasUserAndProfile) return isSuperAdmin ? 'super-admin' : 'dashboard';

  // Not authenticated (yet). On native there's no Landing page to bounce to
  // -- every unauthenticated visit ends at /auth -- so it's safe (and
  // necessary) to hold off choosing until auth has actually resolved.
  if (isNative) {
    if (loading) return null;
    return 'auth';
  }

  // Web: Landing renders immediately for logged-out visitors regardless of
  // `loading`, matching the existing PUBLIC_PREFIXES behavior.
  return 'landing';
}
